// /square/command-center/olivium/portals/:portalId/recipient-preview
//
// "Preview as Recipient" (End User Portal Activation, Phase 7): renders the
// EXACT recipient workspace component fed by the owner-authed preview manifest
// — the same builder the recipient session route uses, marked preview:true.
// No recipient session, invitation, or token exists here; nothing is
// impersonated or consumed. A slim operator bar (the only non-recipient
// chrome) offers the way back.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RecipientStateView } from '../SecureAccess/recipient/RecipientPortal'
import { RECIPIENT_COPY } from '../SecureAccess/recipient/secureInvitation'
import type { RecipientManifest } from '../../api/client'
import { getRecipientPreview, humanizePortalError, portalDetailPath } from './olivium/portal-factory'

const EMPTY_DISPLAY = {
  projectName: null, inviterName: null, purpose: null, expiresAt: null,
  ndaRequired: false, notificationPreferencesRequired: false,
  accountMode: null, fallbackAvailable: false, correlationId: null,
}

export default function SquareCodeRecipientPreviewRoute() {
  const navigate = useNavigate()
  const { portalId = '' } = useParams<{ portalId: string }>()
  const [manifest, setManifest] = useState<RecipientManifest | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!portalId) return
    const ac = new AbortController()
    void getRecipientPreview(portalId, ac.signal)
      .then((r) => {
        if (ac.signal.aborted) return
        if (r.ok && r.manifest) setManifest(r.manifest)
        else setError('Preview unavailable for this portal.')
      })
      .catch((e) => { if (!ac.signal.aborted) setError(humanizePortalError(e)) })
    return () => ac.abort()
  }, [portalId])

  const noop = () => {}
  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
          background: '#0b0d17', color: '#e0b054', fontSize: 12, fontWeight: 650,
        }}
      >
        <button
          type="button"
          onClick={() => navigate(portalDetailPath(portalId))}
          style={{
            appearance: 'none', border: '1px solid rgba(224,176,84,0.5)', borderRadius: 8,
            background: 'transparent', color: '#e0b054', padding: '6px 12px',
            fontSize: 12, fontWeight: 650, cursor: 'pointer',
          }}
        >
          ← Back to portal
        </button>
        <span>Recipient preview — read-only; no invitation or session exists.</span>
        {error ? <span role="alert" style={{ color: '#f08a7a' }}>{error}</span> : null}
      </div>
      <RecipientStateView
        view={manifest ? 'portal_home' : 'preparing'}
        display={EMPTY_DISPLAY}
        manifest={manifest}
        updates={null}
        message={manifest ? RECIPIENT_COPY.portal_home.body : RECIPIENT_COPY.preparing.body}
        busy={false}
        showFallback={false}
        fallbackCode=""
        fallbackNotice={null}
        emailUpdates={false}
        onEmailUpdates={noop}
        onContinue={noop}
        onRetry={noop}
        onRevealFallback={noop}
        onHideFallback={noop}
        onFallbackCode={noop}
        onSubmitFallback={noop}
      />
    </div>
  )
}
